import { useEffect, useState } from 'react';
import { message } from 'antd';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { Skeleton } from '@admin-panel/ui';
import type { LookGroupLookDTOType } from '../../../../schemas/styling';
import {
  useGetLookGroupByIdQuery,
  useMoveLookInGroupMutation,
  useUpdateLookContentMutation,
} from '../../../../store/stylist/stylistApi';
import SortableLookRow from './SortableLookRow';

type LookGroupLooksPanelProps = {
  groupId: number;
  open: boolean;
};

const sortLooks = (looks: Array<LookGroupLookDTOType>) =>
  [...looks].sort((first, second) => {
    const firstOrder = first.group_display_order ?? Number.MAX_SAFE_INTEGER;
    const secondOrder = second.group_display_order ?? Number.MAX_SAFE_INTEGER;

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder;
    }

    return first.id - second.id;
  });

const LookGroupLooksPanel = ({ groupId, open }: LookGroupLooksPanelProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
  );
  const [localLooks, setLocalLooks] = useState<Array<LookGroupLookDTOType>>([]);
  const [updatingActiveLookId, setUpdatingActiveLookId] = useState<number | null>(null);
  const { data, isLoading, isError, refetch } = useGetLookGroupByIdQuery(groupId, {
    skip: !open,
  });
  const [moveLookInGroup, { isLoading: isSavingOrder }] = useMoveLookInGroupMutation();
  const [updateLookContent] = useUpdateLookContentMutation();

  useEffect(() => {
    if (!data?.looks) {
      return;
    }

    setLocalLooks(
      sortLooks(data.looks.filter(look => (look.qc_status ?? 'pending') === 'published')),
    );
  }, [data]);

  const orderedLooks = localLooks;

  const handleActiveChange = async (lookId: number, nextIsActive: boolean) => {
    const previousLooks = orderedLooks;

    setLocalLooks(current =>
      current.map(look => (look.id === lookId ? { ...look, is_active: nextIsActive } : look)),
    );
    setUpdatingActiveLookId(lookId);

    try {
      const updatedLook = await updateLookContent({
        id: lookId,
        data: {
          is_active: nextIsActive,
        },
      }).unwrap();

      setLocalLooks(current =>
        current.map(look =>
          look.id === lookId ? { ...look, is_active: updatedLook.is_active } : look,
        ),
      );
    } catch {
      setLocalLooks(previousLooks);
      message.error('Could not update look active state');
    } finally {
      setUpdatingActiveLookId(null);
    }
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || isSavingOrder) {
      return;
    }

    const activeId = Number(active.id);
    const overId = Number(over.id);
    const oldIndex = orderedLooks.findIndex(look => look.id === activeId);
    const newIndex = orderedLooks.findIndex(look => look.id === overId);

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const previousLooks = orderedLooks;
    const nextLooks = [...orderedLooks];
    const [movedLook] = nextLooks.splice(oldIndex, 1);

    if (!movedLook) {
      return;
    }

    nextLooks.splice(newIndex, 0, movedLook);

    const beforeId = newIndex > 0 ? nextLooks[newIndex - 1]?.id : undefined;
    const afterId = newIndex < nextLooks.length - 1 ? nextLooks[newIndex + 1]?.id : undefined;

    setLocalLooks(nextLooks);
    message.loading({
      key: `occasion-look-reorder-${groupId}`,
      content: 'Saving look order...',
      duration: 0,
    });

    try {
      await moveLookInGroup({
        id: groupId,
        lookId: activeId,
        data: {
          before_id: beforeId ?? null,
          after_id: afterId ?? null,
        },
      }).unwrap();

      const refreshedGroupResult = await refetch();
      if ('data' in refreshedGroupResult && refreshedGroupResult.data?.looks) {
        setLocalLooks(
          sortLooks(
            refreshedGroupResult.data.looks.filter(
              look => (look.qc_status ?? 'pending') === 'published',
            ),
          ),
        );
      }
      message.success({
        key: `occasion-look-reorder-${groupId}`,
        content: 'Look order updated',
      });
    } catch {
      setLocalLooks(previousLooks);
      message.error({
        key: `occasion-look-reorder-${groupId}`,
        content: 'Could not update look order',
      });
    }
  };

  if (!open) {
    return null;
  }

  if (isLoading && !data) {
    return (
      <div className="flex gap-2 overflow-hidden px-0 py-1">
        <Skeleton className="aspect-2/3 w-30  shrink-0 rounded-md" />
        <Skeleton className="aspect-2/3 w-30  shrink-0 rounded-md" />
        <Skeleton className="aspect-2/3 w-30  shrink-0 rounded-md" />
        <Skeleton className="aspect-2/3 w-30  shrink-0 rounded-md" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-1 text-xs text-muted-foreground">Could not load looks for this group.</div>
    );
  }

  if (orderedLooks.length === 0) {
    return (
      <div className="py-1 text-xs text-muted-foreground">
        No published looks found in this group.
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={orderedLooks.map(look => look.id)}
        strategy={horizontalListSortingStrategy}
      >
        <div className="flex gap-2 overflow-x-auto">
          {orderedLooks.map(look => (
            <SortableLookRow
              key={look.id}
              look={look}
              isBusy={isSavingOrder}
              isUpdatingActive={updatingActiveLookId === look.id}
              onActiveChange={handleActiveChange}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default LookGroupLooksPanel;
